// E34 — "Vardiyalarım": the employee's own published shifts.
//
// The "Çalışma programı yayımlandı" notification deep-links to
// /me/attendance?schedule_id=…, and until this card existed nothing on that
// page read the parameter — an employee told their roster was out had no
// screen that showed it (Planlama finds meeting times; the calendar shows only
// events one owns; the manager roster is /schedules). This is the smallest
// honest surface: the caller's own assigned shifts, nothing else.
//
// Two modes, one query. With `scheduleId` (the notification) it lists that
// roster's shifts including days already past, because a late opener wants to
// see the whole roster they were told about. Without it, the upcoming shifts
// of every published roster. Both are pinned to the caller and to published
// schedules on the server (fetchOwnShiftsPage).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock } from "lucide-react";

import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  type DataTableColumn,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { formatEventTime } from "@/features/calendar/format";
import { fetchOwnShiftsPage, type ShiftRow } from "./time-api";
import { SHIFT_STATUS_TONE } from "./time-status";

export interface MyShiftsCardProps {
  readonly employeeId: string;
  /** From the notification link; null lists the upcoming published shifts. */
  readonly scheduleId: string | null;
}

export function MyShiftsCard({ employeeId, scheduleId }: MyShiftsCardProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  // Fixed for the life of the card: a "now" recomputed per render would change
  // the request on every render and refetch forever.
  const [endsAfter] = useState(() => new Date().toISOString());

  const query = useKeysetQuery<ShiftRow>({
    queryKey: ["time", "shifts", "own", employeeId, scheduleId],
    fetchPage: (cursor) => fetchOwnShiftsPage(employeeId, { scheduleId, endsAfter }, cursor),
  });

  // The server pages shifts by creation time. One person's published, upcoming
  // shifts fit a page, so ordering the window by start is the order they will
  // actually work them in.
  const rows = useMemo(
    () => [...query.items].sort((a, b) => a.start_at.localeCompare(b.start_at)),
    [query.items],
  );

  const columns: readonly DataTableColumn<ShiftRow>[] = [
    {
      id: "window",
      header: t("time.my_shifts.column.window"),
      cell: (row) => (
        <div className="text-[13px] text-[var(--fg-secondary)]">
          <div>{formatEventTime(row.start_at, timeZone, i18n.language)}</div>
          <div className="text-[var(--fg-muted)]">
            {formatEventTime(row.end_at, timeZone, i18n.language)}
          </div>
        </div>
      ),
    },
    {
      id: "role",
      header: t("time.my_shifts.column.role"),
      cell: (row) => (
        <span className="text-[13px] text-[var(--fg-secondary)]">
          {row.role ?? t("time.my_shifts.no_role")}
        </span>
      ),
    },
    {
      id: "status",
      header: t("time.my_shifts.column.status"),
      cell: (row) => (
        <Badge tone={SHIFT_STATUS_TONE[row.status] ?? "neutral"}>
          {t(`time.shift_status.${row.status}`)}
        </Badge>
      ),
    },
  ];

  return (
    <Card>
      <div className="space-y-4 p-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: "var(--accent-subtle-bg)" }}
            aria-hidden="true"
          >
            <CalendarClock size={20} color="var(--accent)" />
          </div>
          <div>
            <h2 className="font-serif text-[16px] text-[var(--fg-primary)]">
              {t("time.my_shifts.title")}
            </h2>
            <p className="font-sans text-[13px] text-[var(--fg-muted)]">
              {scheduleId === null
                ? t("time.my_shifts.description_upcoming")
                : t("time.my_shifts.description_schedule")}
            </p>
          </div>
        </div>

        {query.isLoading ? (
          <Loading label={t("time.my_shifts.loading")} rows={3} />
        ) : query.isError ? (
          <ErrorState
            title={t("time.my_shifts.error_title")}
            description={errorMessage(query.error, t)}
          />
        ) : rows.length > 0 ? (
          <DataTable<ShiftRow>
            caption={t("time.my_shifts.table_caption")}
            columns={columns}
            rows={rows}
            rowKey={(row): string => row.shift_id}
            hasNextPage={query.hasNextPage}
            onLoadMore={query.fetchNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            loadMoreLabel={t("time.load_more")}
          />
        ) : (
          <Empty
            title={
              scheduleId === null
                ? t("time.my_shifts.empty_title")
                : t("time.my_shifts.empty_schedule_title")
            }
            description={
              scheduleId === null
                ? t("time.my_shifts.empty_description")
                : t("time.my_shifts.empty_schedule_description")
            }
          />
        )}
      </div>
    </Card>
  );
}
